import { I18nTranslator, translations } from "utils/i18n";
import Webcam from "react-webcam";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import {
  ElementRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import { Camera } from "@mui/icons-material";

interface IPhotoCaptureModal {
  open?: boolean;
  onSavePhoto: (photoBase64: string) => void;
  onClose: () => void;
  selectedImage?: string;
}
export const PhotoCaptureModal = ({
  open,
  onSavePhoto,
  onClose,
  selectedImage,
}: IPhotoCaptureModal) => {
  const [capturedImage, setCapturedImage] = useState<string>();
  const [cameraError, setCameraError] = useState<string>();
  // const [isLoading, setIsLoading] = useState<boolean>();
  const [isImageLoadError, setIsImageLoadError] = useState<boolean>();
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
  const [availableImageHeight, setAvailableImageHeight] = useState<number>();
  const [imageButtonHeight, setImageButtonHeight] = useState<number>();
  const measureImageContainer = useCallback((ref: HTMLElement | null) => {
    if (ref !== null) {
      setAvailableImageHeight(ref.clientHeight);
    }
  }, []);
  const measureImageButtonContainer = useCallback((ref: HTMLElement | null) => {
    if (ref !== null) {
      setImageButtonHeight(ref.clientHeight);
    }
  }, []);
  const cameraRef = useRef<any>(null);

  useEffect(() => {
    if (open) {
      if (selectedImage && selectedImage.includes("http")) {
        setIsImageLoading(true);
      }
      setIsImageLoadError(undefined);
      setCameraError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setCapturedImage(selectedImage);
  }, [selectedImage]);

  const onSaveClick = () => {
    if (selectedImage && selectedImage === capturedImage) {
      onClose();
    } else {
      onSavePhoto(capturedImage!);
      setCapturedImage(undefined);
    }
  };

  return (
    <>
      <Modal
        open={open || false}
        onClose={() => {
          onClose();
          setCapturedImage(undefined);
        }}
        style={{
          height: "90%",
          width: "90%",
          margin: "auto",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            maxHeight: "100%",
            bgcolor: "white",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            boxShadow: "6px rgb(0 0 0 / 4%)",
            overflow: "hidden",
          }}
        >
          <Grid
            sx={{
              // mb: 2,
              p: 2,
            }}
            display="flex"
          >
            <Typography variant="h2">
              {I18nTranslator(
                translations.inProcessCheckReportDetail.capturePhoto
              )}
            </Typography>
            {/* {selectedImage && ( */}
            <Grid flex={1} textAlign={"right"}>
              <IconButton
                onClick={() => {
                  onClose();
                  setCapturedImage(undefined);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            {/* )} */}
          </Grid>

          <Grid
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={
              !capturedImage && !cameraError
                ? () => {
                    setCapturedImage(cameraRef.current?.getScreenshot()!);
                  }
                : undefined
            }
            container
            spacing={2}
          >
            {!capturedImage || cameraError ? (
              cameraError ? (
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  direction="column"
                >
                  <ReportGmailerrorredIcon color="error" fontSize="large" />
                  <Typography variant="h3" color="red">
                    {cameraError}
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid flex={1}>
                    <Webcam
                      screenshotFormat="image/jpeg"
                      onUserMediaError={(error) =>
                        setCameraError((error as DOMException).message)
                      }
                      ref={cameraRef}
                      height="100%"
                    />
                  </Grid>
                </>
              )
            ) : (
              <Grid
                // item
                // container
                alignItems="center"
                justifyContent="center"
                flex={1}
                display="flex"
                flexDirection="column"
                width={"100%"}
                // spacing={2}
              >
                {!isImageLoadError && (
                  // eslint-disable-next-line jsx-a11y/alt-text
                  <Grid
                    // item
                    flex={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ref={measureImageContainer}
                  >
                    <img
                      src={capturedImage}
                      onError={(e) => {
                        setIsImageLoading(false);
                        setIsImageLoadError(true);
                      }}
                      onLoad={() => {
                        setIsImageLoading(false);
                      }}
                      height={
                        (availableImageHeight || 0) - (imageButtonHeight || 0)
                      }
                      style={{ objectFit: "contain" }}
                      alt=""
                    />
                    {isImageLoading && (
                      <>
                        <CircularProgress
                          color={"primary"}
                          style={{
                            marginBottom: 20,
                          }}
                        />
                        <Typography color="primary" fontWeight="bold">
                          {I18nTranslator(
                            translations.inProcessCheckReportDetail.loadingImage
                          )}
                        </Typography>
                      </>
                    )}
                  </Grid>
                )}
                {isImageLoadError && (
                  <>
                    <ErrorIcon style={{ fontSize: 96 }} color="error" />
                    <Typography
                      color="error"
                      fontWeight="bold"
                      whiteSpace="pre-line"
                      textAlign="center"
                    >
                      {I18nTranslator(
                        translations.inProcessCheckReportDetail.imageError
                      )}
                    </Typography>
                  </>
                )}
                <Grid
                  item
                  ref={measureImageButtonContainer}
                  marginBottom={2}
                  textAlign={"center"}
                >
                  <LoadingButton
                    variant="contained"
                    onClick={() => {
                      onSaveClick();
                    }}
                    startIcon={
                      selectedImage && selectedImage === capturedImage ? (
                        <CloseIcon />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    // loading={isLoading}
                  >
                    {I18nTranslator(
                      selectedImage && selectedImage === capturedImage
                        ? translations.button.close
                        : translations.button.save
                    )}
                  </LoadingButton>
                  {capturedImage && !capturedImage.includes("http") && (
                    <LoadingButton
                      variant="contained"
                      onClick={() => {
                        setCapturedImage(undefined);
                      }}
                      startIcon={<Camera />}
                      // loading={isLoading}
                      style={{ marginLeft: 16 }}
                    >
                      {I18nTranslator(
                        translations.inProcessCheckReportDetail.reCapturePhoto
                      )}
                    </LoadingButton>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>
      </Modal>
    </>
  );
};
